import React, { Fragment, useEffect } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { Paper, Drawer, List, ListItem, ListItemText, CircularProgress, Divider, Box, Typography, Avatar } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import AdbIcon from '@mui/icons-material/Adb';
import HistoryIcon from '@mui/icons-material/History';
import InsightsIcon from '@mui/icons-material/Insights';
import LogoutIcon from '@mui/icons-material/Logout';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemButton from '@mui/material/ListItemButton';
import { loadHandles } from '../handles/actions';
import { logout } from '../auth/actions';
import { useDispatch, useSelector } from 'react-redux';
import Overlay from '../components/overlay/Overlay';

const drawerWidth = 240;

function MainPage() {
    const dispatch = useDispatch();
    const location = useLocation();
    const user = useSelector(state => state.auth.user);
    const userId = user?.uid;
    const userEmail = user?.email || '';
    const handles = useSelector(state => state.handle.handles);

    useEffect(() => {
        dispatch(loadHandles(userId));
    }, [dispatch, userId]);

    const isPathSelected = (path) => {
        return location.pathname.startsWith(path);
    };

    const handleLogout = () => {
        dispatch(logout());
    };

    // Get user initials for avatar
    const getUserInitials = (email) => {
        if (!email) return 'U';
        const parts = email.split('@')[0].split('.');
        if (parts.length >= 2) {
            return (parts[0][0] + parts[1][0]).toUpperCase();
        }
        return email[0].toUpperCase();
    };

    return (
        <Fragment>
            <Overlay isOpen={!handles}>
                <CircularProgress sx={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }} />
            </Overlay>
            {handles != null ?
                <Paper sx={{ display: 'flex', height: '100%', position: 'fixed', minWidth: '100%', boxShadow: 'none' }}>
                    <Drawer variant="permanent"
                        sx={{
                            width: drawerWidth,
                            flexShrink: 0,
                            '& .MuiDrawer-paper': {
                                width: drawerWidth,
                                boxSizing: 'border-box',
                                display: 'flex',
                                flexDirection: 'column',
                            },
                        }}
                    >
                        {/* User Profile Header */}
                        <Box 
                            sx={{ 
                                p: 2.5, 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 1.5,
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                position: 'relative',
                                '&::after': {
                                    content: '""',
                                    position: 'absolute',
                                    bottom: 0,
                                    left: 0,
                                    right: 0,
                                    height: '1px',
                                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                                }
                            }}
                        >
                            <Avatar 
                                sx={{ 
                                    width: 44,
                                    height: 44,
                                    fontSize: '1.1rem',
                                    border: '2px solid rgba(255, 255, 255, 0.3)',
                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                }}
                            >
                                {getUserInitials(userEmail)}
                            </Avatar>
                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                <Typography 
                                    variant="body2" 
                                    fontWeight={700}
                                    sx={{ 
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        color: 'white',
                                    }}
                                >
                                    {userEmail.split('@')[0]}
                                </Typography>
                                <Typography 
                                    variant="caption" 
                                    sx={{ 
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        display: 'block',
                                        color: 'rgba(255, 255, 255, 0.8)',
                                    }}
                                >
                                    {userEmail}
                                </Typography>
                            </Box>
                        </Box>

                        <Divider />

                        <List sx={{ flexGrow: 1, overflowY: 'auto' }}>
                            <ListItem button component={Link} to="/" selected={location.pathname === '/' || location.pathname === '/dashboard'}>
                                <ListItemButton sx={{ m: -1, ml: -2, mr: -2 }}>
                                    <ListItemIcon>
                                        <HomeIcon />
                                    </ListItemIcon>
                                    <ListItemText primary="Home" />
                                </ListItemButton>
                            </ListItem>
                            
                            <Divider sx={{ my: 1 }} />
                            
                            {handles.length > 0 ?
                                <Fragment>
                                    <ListItem button component={Link} to={`/growth?handle=${handles[0].handle}`} selected={isPathSelected('/growth')}>
                                        <ListItemButton sx={{ m: -1, ml: -2, mr: -2 }}>
                                            <ListItemIcon>
                                                <TrendingUpIcon />
                                            </ListItemIcon>
                                            <ListItemText primary="Growth" />
                                        </ListItemButton>
                                    </ListItem>
                                    <ListItem button component={Link} to={`/analytics?handle=${handles[0].handle}`} selected={isPathSelected('/analytics')}>
                                        <ListItemButton sx={{ m: -1, ml: -2, mr: -2 }}>
                                            <ListItemIcon>
                                                <InsightsIcon />
                                            </ListItemIcon>
                                            <ListItemText primary="Analytics" />
                                        </ListItemButton>
                                    </ListItem>
                                    <ListItem button component={Link} to={`/bot-activity?handle=${handles[0].handle}`} selected={isPathSelected('/bot-activity')}>
                                        <ListItemButton sx={{ m: -1, ml: -2, mr: -2 }}>
                                            <ListItemIcon>
                                                <HistoryIcon />
                                            </ListItemIcon>
                                            <ListItemText primary="Bot Activity" />
                                        </ListItemButton>
                                    </ListItem>
                                    <ListItem button component={Link} to={`/scheduler?handle=${handles[0].handle}`} selected={isPathSelected('/scheduler')}>
                                        <ListItemButton sx={{ m: -1, ml: -2, mr: -2 }}>
                                            <ListItemIcon>
                                                <CalendarMonthIcon />
                                            </ListItemIcon>
                                            <ListItemText primary="Scheduler" />
                                        </ListItemButton>
                                    </ListItem>
                                    <ListItem button component={Link} to={`/bots?handle=${handles[0].handle}`} selected={isPathSelected('/bots')}>
                                        <ListItemButton sx={{ m: -1, ml: -2, mr: -2 }}>
                                            <ListItemIcon>
                                                <AdbIcon />
                                            </ListItemIcon>
                                            <ListItemText primary="Bots" />
                                        </ListItemButton>
                                    </ListItem>
                                    
                                    <Divider sx={{ my: 1 }} />
                                </Fragment> : null}
                            
                            <ListItem button component={Link} to="/handles" selected={isPathSelected('/handles')}>
                                <ListItemButton sx={{ m: -1, ml: -2, mr: -2 }}>
                                    <ListItemIcon>
                                        <ManageAccountsIcon />
                                    </ListItemIcon>
                                    <ListItemText primary="Handles" />
                                </ListItemButton>
                            </ListItem>
                        </List>

                        {/* Logout Button at Bottom */}
                        <Divider />
                        <List>
                            <ListItem button onClick={handleLogout}>
                                <ListItemButton sx={{ m: -1, ml: -2, mr: -2 }}>
                                    <ListItemIcon>
                                        <LogoutIcon />
                                    </ListItemIcon>
                                    <ListItemText primary="Logout" />
                                </ListItemButton>
                            </ListItem>
                        </List>
                    </Drawer>
                    <Paper sx={{ 
                        flexGrow: 1, 
                        alignSelf: 'stretch', 
                        p: 0, 
                        overflowY: 'scroll',
                        backgroundColor: '#f8fafc',
                        boxShadow: 'none',
                    }}>
                        <Outlet />
                    </Paper>
                </Paper> : undefined}
        </Fragment>
    );
}

export default MainPage;
